import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CalendarIcon } from 'lucide-react'
import {
	Form, FormControl, FormField, FormItem, FormLabel,
	Input,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { DEFAULT_LOCALE, DOCUMENT_TYPES, DOCUMENT_TYPE_KEYS } from '@/types'
import type { BasicsValues } from './types'

const basicsSchema = z.object({
	number:       z.string(),
	dateIso:      z.string(),
	documentType: z.string().min(1),
})

type BasicsSchema = z.infer<typeof basicsSchema>

function formatDateDisplay(iso: string): string {
	if (!iso) return ''
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return iso
	return d.toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

interface StepBasicsProps {
	initial: BasicsValues
	/** Numbers that already exist in the system — used for duplicate-number warning. */
	existingNumbers: string[]
	/** Form id lifted to the wizard so the footer's Далее button can submit this form. */
	formId: string
	onValid: (values: BasicsValues) => void
}

export function StepBasics({ initial, existingNumbers, formId, onValid }: StepBasicsProps) {
	const { t } = useTranslation()

	const form = useForm<BasicsSchema>({
		resolver: zodResolver(basicsSchema),
		defaultValues: initial,
		mode: 'onChange',
	})

	const numberValue = form.watch('number')
	const dateValue   = form.watch('dateIso')
	const showConflict = numberValue.length > 0 && existingNumbers.includes(numberValue)

	function handleSubmit(values: BasicsSchema) {
		onValid(values)
	}

	return (
		<Form {...form}>
			<form id={formId} onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
				<FormField
					control={form.control}
					name="documentType"
					render={({ field }) => (
						<FormItem>
							<FormLabel className="text-sm leading-5 font-[500] text-(--color-foreground)">
								{t('deals.documents.wizard.basics.documentType')}
							</FormLabel>
							<FormControl>
								<Select value={field.value} onValueChange={field.onChange}>
									<SelectTrigger>
										<SelectValue placeholder={t('deals.documents.wizard.basics.documentType')} />
									</SelectTrigger>
									<SelectContent>
										{DOCUMENT_TYPES.map((dt) => (
											<SelectItem key={dt} value={dt}>
												{t(DOCUMENT_TYPE_KEYS[dt])}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormControl>
						</FormItem>
					)}
				/>

				<div className="flex gap-2">
					<FormField
						control={form.control}
						name="number"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel className="text-sm leading-5 font-[500] text-(--color-foreground)">
									{t('deals.documents.wizard.basics.number')}
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										prefix={t('deals.documents.wizard.basics.numberPrefix')}
									/>
								</FormControl>
								{showConflict && (
									<p className="text-sm leading-5 text-(--color-warning)">
										{t('deals.documents.wizard.basics.numberConflict')}
									</p>
								)}
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="dateIso"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel className="text-sm leading-5 font-[500] text-(--color-foreground)">
									{t('deals.documents.wizard.basics.date')}
								</FormLabel>
								<FormControl>
									<label className="flex cursor-pointer items-center gap-2 rounded-(--radius-md) border border-(--color-input) bg-(--color-background) px-4 py-2.5 focus-within:ring-2 focus-within:ring-(--color-ring)">
										<span className="text-sm leading-5 text-(--color-muted-foreground)">
											{t('deals.documents.wizard.basics.datePrefix')}
										</span>
										<span className="flex-1 text-sm leading-5 text-(--color-foreground)">
											{formatDateDisplay(dateValue)}
										</span>
										<input
											{...field}
											type="date"
											className="sr-only"
										/>
										<CalendarIcon className="size-4 text-(--color-muted-foreground)" />
									</label>
								</FormControl>
							</FormItem>
						)}
					/>
				</div>
			</form>
		</Form>
	)
}
